import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";
import { getOrderStateStyle } from "../../../../utils/orderStyles";

// Constants to match OrderLine positioning
const OFFSET_CONFIG = {
  BUTTON_AREA_WIDTH: 220,
  BUTTON_WIDTH: 30,
  BUTTON_HEIGHT: 20,
  OFFSET_FROM_EDGE: 58, // Distance from price scale edge (#119) - must match OrderLine
};

// OCO Connector Line component - renders vertical lines connecting linked OCO orders
class OCOConnectorLine extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
  }

  // Calculate delete button X position using same logic as OrderLine
  getDeleteButtonX(order, width, offsetPixels = 0) {
    const offsetFromEdge = OFFSET_CONFIG.OFFSET_FROM_EDGE;

    if (order.state === "open") {
      const openOrderWidth = OFFSET_CONFIG.BUTTON_WIDTH + 80;
      return width - openOrderWidth - offsetFromEdge - offsetPixels;
    } else {
      return (
        width - OFFSET_CONFIG.BUTTON_AREA_WIDTH - offsetFromEdge - offsetPixels
      );
    }
  }

  // Calculate order offset using same logic as OrderLine
  calculateOrderOffset(order, y, allOrders, moreProps) {
    const SPACING_BETWEEN_LEVELS = 30;
    const BASE_PROXIMITY_THRESHOLD = 15;

    // Find nearby orders
    const nearbyOrders = allOrders.filter((otherOrder) => {
      if (otherOrder.id === order.id) return false;

      const {
        chartConfig: { yScale },
      } = moreProps;
      const otherY = yScale(otherOrder.price);
      const distance = Math.abs(y - otherY);

      return distance <= BASE_PROXIMITY_THRESHOLD;
    });

    if (nearbyOrders.length === 0) return 0;

    // Sort nearby orders by price (ascending)
    const sortedNearbyOrders = nearbyOrders.sort((a, b) => a.price - b.price);

    // Find this order's position in the sorted list including itself
    const allNearbyOrders = [...sortedNearbyOrders, order].sort(
      (a, b) => a.price - b.price,
    );
    const orderIndex = allNearbyOrders.findIndex((o) => o.id === order.id);

    // Apply offset based on position in the stack
    return orderIndex * SPACING_BETWEEN_LEVELS;
  }

  // Convert strokeDasharray to canvas dash pattern
  getCanvasDashPattern(strokeDasharray) {
    if (!strokeDasharray || strokeDasharray === "none") {
      return []; // Solid line
    }

    // Parse common dash patterns
    if (strokeDasharray === "1,2") {
      return [2, 4]; // Short dash, medium gap
    } else if (strokeDasharray === "5,5") {
      return [5, 5]; // Medium dash, medium gap
    } else if (strokeDasharray === "10,5") {
      return [10, 5]; // Long dash, short gap
    }

    // Try to parse numeric pattern "x,y"
    const parts = strokeDasharray.split(",");
    if (parts.length >= 2) {
      const dash = parseFloat(parts[0]);
      const gap = parseFloat(parts[1]);
      if (!isNaN(dash) && !isNaN(gap)) {
        return [dash, gap];
      }
    }

    return []; // Fallback to solid line
  }

  drawOnCanvas(ctx, moreProps) {
    const { order1, order2, allOrders = [] } = this.props;

    if (!order1 || !order2) {
      return;
    }

    const {
      chartConfig: { width, yScale },
    } = moreProps;

    if (!yScale || typeof yScale !== "function") {
      return;
    }

    // Get Y positions for both orders
    const y1 = yScale(order1.price);
    const y2 = yScale(order2.price);

    // Calculate offset pixels for both orders (same logic as OrderLine)
    const offsetPixels1 = this.calculateOrderOffset(
      order1,
      y1,
      allOrders,
      moreProps,
    );
    const offsetPixels2 = this.calculateOrderOffset(
      order2,
      y2,
      allOrders,
      moreProps,
    );

    // Get delete button X positions for both orders
    const deleteButtonX1 = this.getDeleteButtonX(order1, width, offsetPixels1);
    const deleteButtonX2 = this.getDeleteButtonX(order2, width, offsetPixels2);

    // Use the leftmost delete button X position for the connector line
    const connectorX = Math.min(deleteButtonX1, deleteButtonX2);

    // Move connector line a bit more to the left to align with background border
    const adjustedConnectorX = connectorX - 10;

    // Get line style based on the primary order's state (typically the first order)
    const stateStyle = getOrderStateStyle(order1.state);
    const dashPattern = this.getCanvasDashPattern(stateStyle.strokeDasharray);

    // Set line style - subtle gray color for OCO connectors
    ctx.setLineDash(dashPattern);
    ctx.lineWidth = 1;
    ctx.strokeStyle = stateStyle.stroke || "#666666";
    ctx.globalAlpha = (stateStyle.opacity || 0.7) * 0.6; // Make OCO connectors more subtle

    // Draw vertical connector line only (no horizontal markers)
    ctx.beginPath();
    ctx.moveTo(adjustedConnectorX, Math.min(y1, y2));
    ctx.lineTo(adjustedConnectorX, Math.max(y1, y2));
    ctx.stroke();

    // Draw small horizontal lines connecting toward OCO labels but stopping before background
    // Calculate where the label backgrounds start
    const labelBackgroundX1 =
      this.getDeleteButtonX(order1, width, offsetPixels1) - 8; // Stop 8px before label background
    const labelBackgroundX2 =
      this.getDeleteButtonX(order2, width, offsetPixels2) - 8; // Stop 8px before label background

    // Calculate horizontal line length - stop before reaching the label background
    const horizontalLength1 = Math.max(
      5,
      labelBackgroundX1 - adjustedConnectorX - 5,
    ); // Minimum 5px, with 5px gap
    const horizontalLength2 = Math.max(
      5,
      labelBackgroundX2 - adjustedConnectorX - 5,
    ); // Minimum 5px, with 5px gap

    // Horizontal line for order 1
    ctx.beginPath();
    ctx.moveTo(adjustedConnectorX, y1);
    ctx.lineTo(adjustedConnectorX + horizontalLength1, y1);
    ctx.stroke();

    // Horizontal line for order 2
    ctx.beginPath();
    ctx.moveTo(adjustedConnectorX, y2);
    ctx.lineTo(adjustedConnectorX + horizontalLength2, y2);
    ctx.stroke();

    // Reset canvas state
    ctx.setLineDash([]);
    ctx.globalAlpha = 1.0;
  }

  renderSVG() {
    return null; // Canvas-only implementation
  }

  render() {
    return (
      <GenericChartComponent
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        drawOn={["mousemove", "pan", "drag", "zoom", "xAxisZoom", "resize"]}
        svgDraw={this.renderSVG}
      />
    );
  }
}

OCOConnectorLine.propTypes = {
  order1: PropTypes.shape({
    id: PropTypes.string.isRequired,
    side: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    state: PropTypes.string.isRequired,
  }).isRequired,
  order2: PropTypes.shape({
    id: PropTypes.string.isRequired,
    side: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    state: PropTypes.string.isRequired,
  }).isRequired,
  allOrders: PropTypes.array,
};

OCOConnectorLine.defaultProps = {
  allOrders: [],
};

export default OCOConnectorLine;
